<template>
    <div class="theme-switcher">
        <el-popover
            placement="bottom"
            width="200"
            trigger="hover"
            v-model="visible">
            <div class="theme-grid">
                <div
                    v-for="color in themes"
                    :key="color"
                    class="color-item"
                    :class="{ active: currentColor === color }"
                    @click="handleSelect(color)">
                    <span class="color-circle" :style="{ backgroundColor: color }"></span>
                    <i v-if="currentColor === color" class="el-icon-check check-icon"></i>
                </div>
            </div>
            <div slot="reference" class="trigger">
                <span class="current-circle" :style="{ backgroundColor: currentColor }"></span>
            </div>
        </el-popover>
    </div>
</template>

<script>
import { getThemePreference, saveThemePreference } from '@/api/modules/theme';
import { applyThemeColor, COOKIE_KEY } from '@/utils/theme';
import Cookies from 'js-cookie';

const STORAGE_KEY = 'app_theme_primary_color';
const DEFAULT_COLOR = '#304e72';

// 用户可选主题色（与后端持久化保存一致，包含原默认色 #304e72）
const PRESET_THEMES = ['#304e72', '#205FE7', '#A1CCF4', '#4096FB', '#143071'];

export default {
    name: 'ThemeSwitcher',
    data() {
        return {
            visible: false,
            themes: PRESET_THEMES,
            currentColor: DEFAULT_COLOR
        };
    },
    mounted() {
        // 仅从 localStorage 恢复当前选中态用于显示，不覆盖已由 restoreThemeFromStorage/syncThemeFromServer 设置的主题色
        const saved = window.localStorage.getItem(STORAGE_KEY);
        this.currentColor = saved || DEFAULT_COLOR;
        // 挂到 window，让路由守卫可以触发服务器同步
        window.__themeSwitcher = this;
        // 主动从服务器同步一次（覆盖路由守卫可能遗漏的场景，如 localStorage 被清除后重新登录）
        const token = localStorage.getItem('token');
        if (token) {
            this.syncFromServer();
        }
    },
    beforeDestroy() {
        if (window.__themeSwitcher === this) {
            delete window.__themeSwitcher;
        }
    },
    methods: {
        /**
         * 从服务器同步主题色（登录后由路由守卫通过 syncThemeFromServer 调用）
         */
        async syncFromServer() {
            try {
                const res = await getThemePreference();
                if (res && res.state && res.state.code === 200 && res.result && res.result.themeColor) {
                    const color = res.result.themeColor;
                    this.applyTheme(color);
                    window.localStorage.setItem(STORAGE_KEY, color);
                    Cookies.set(COOKIE_KEY, color, { expires: 365 });
                }
            } catch (e) {
                // 静默失败，使用本地缓存
                console.warn('[theme] 同步服务器主题色失败：', e);
            }
        },
        applyTheme(color) {
            applyThemeColor(color);
            this.currentColor = color;
        },
        async handleSelect(color) {
            this.applyTheme(color);
            window.localStorage.setItem(STORAGE_KEY, color);
            Cookies.set(COOKIE_KEY, color, { expires: 365 });
            this.visible = false;
            // 保存到后端（无 token 时跳过）
            const token = localStorage.getItem('token');
            if (!token) return;
            try {
                await saveThemePreference(color);
            } catch (e) {
                // 保存失败仅提示，不影响本地应用
                console.warn('[theme] 保存到服务器失败：', e);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.theme-switcher {
    display: flex;
    align-items: center;

    .trigger {
        cursor: pointer;
        display: flex;
        align-items: center;
        padding: 4px;
    }

    .current-circle {
        display: inline-block;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid #fff;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
        transition: transform 0.2s;

        &:hover {
            transform: scale(1.1);
        }
    }
}

.theme-grid {
    display: flex;
    justify-content: space-around;
    align-items: center;
    padding: 4px 0;

    .color-item {
        position: relative;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
            background-color: #f5f5f5;
        }

        &.active {
            background-color: #e8f4ff;
        }

        .color-circle {
            display: inline-block;
            width: 22px;
            height: 22px;
            border-radius: 50%;
            border: 1px solid rgba(0, 0, 0, 0.15);
        }

        .check-icon {
            position: absolute;
            bottom: -2px;
            right: -2px;
            color: #4096FB;
            font-size: 14px;
            font-weight: bold;
            background: #fff;
            border-radius: 50%;
            padding: 1px;
        }
    }
}
</style>
